import { useMutation, useReactiveVar } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import FormInput from '../../../components/FormInput/FormInput';
import { ResendCode, ResendCodeTimer } from '../../../components/FormInput/styled';
import { FORM_INPUT_COMPONENT_TYPES, FORM_INPUT_TYPES } from '../../../components/FormInput/types';
import useUserProfile from '../../../hooks/useUserProfile';
import { vars } from '../../../reactive';
import { replaceVariables, RESEND_VERIFICATION_EMAIL_COUNTER } from '../../../utils';
import AppStorage from '../../../utils/AppStorage';
import getUserLocale from '../../../utils/getUserLocale';
import { SendVerificationEmail, UpdateUserInfo, VerifyUser } from '../../../utils/queries';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';

const FormAccountCode = () => {
  const type = FORM_INPUT_TYPES.CODE;
  const { title } = FORM_INPUT_COMPONENT_TYPES[type];
  const account = vars.newAccount();
  const resendCounter = RESEND_VERIFICATION_EMAIL_COUNTER;
  const lastEmailResend = useReactiveVar(vars.lastEmailResend);
  const [resendTimes, setResendTimes] = useState(0);
  const [allowResendCounter, setAllowResendCounter] = useState(0);

  const { register, handleSubmit, setFocus, formState, reset, setValue, control } = useForm();

  const [sendVerificationEmail] = useMutation(SendVerificationEmail, {
    fetchPolicy: 'no-cache'
  });
  const [verifyUser, { loading: verifyUserLoading, error: verifyUserError, data: verifyUserData }] = useMutation(VerifyUser, {
    fetchPolicy: 'no-cache'
  });

  const [getProfile, { loading: getProfileLoading }] = useUserProfile(true);
  const [updateUser] = useMutation(UpdateUserInfo);

  const onSubmit = (data: Record<string, Array<string>>) => {
    const fixedCode = data.CODE.join('');
    trackEvent(AnalyticsEvents.CODE_INPUT);

    verifyUser({
      variables: { code: fixedCode, email: account.email }
    });
  };

  useEffect(() => {
    //resend
    const waitTime = resendCounter[0] - (Date.now() - lastEmailResend);
    if (allowResendCounter <= 0) {
      setAllowResendCounter(waitTime);
    }
    const timer = setInterval(() => {
      setAllowResendCounter(counter => {
        const next = counter - 1000;
        if (next <= 0) clearInterval(timer);
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lastEmailResend]);

  const resendHandler = () => {
    if (Date.now() - lastEmailResend > resendCounter[0]) {
      vars.lastEmailResend(Date.now() + resendCounter[resendTimes]);

      setResendTimes(resendCounter[resendTimes + 1] ? resendTimes + 1 : resendTimes);
      sendVerificationEmail({ variables: { email: account.email } });
    }
  };

  useEffect(() => {
    const saveTokens = async () => {
      if (verifyUserData?.verifyAppUser) {
        trackEvent(AnalyticsEvents.CODE_VERIFIED);
        if (!account.exists) {
          trackEvent(AnalyticsEvents.ACCOUNT_CREATED);
        }
        AppStorage.set('tokens', {
          token: verifyUserData.verifyAppUser.token,
          firebaseToken: verifyUserData.verifyAppUser.firebaseToken,
          refreshToken: verifyUserData.verifyAppUser.refreshToken
        });
        vars.isLoggedIn(true);
        await getProfile();
        updateUser({ variables: { locale: getUserLocale() } });
      }
    };
    saveTokens();
  }, [verifyUserData]);

  return (
    <FormInput
      type={type}
      formOptions={{
        register,
        handleSubmit,
        setFocus,
        reset,
        formState,
        setValue,
        control
      }}
      onSubmit={onSubmit}
      title={replaceVariables(title, { email: account.email })}
      loading={verifyUserLoading || getProfileLoading}
      error={verifyUserError}
      previousPage={{
        page: FORM_PAGES.CREATE_ACCOUNT_EMAIL,
        animation: 'fade-out'
      }}
      accessibility={false}
      nextButtonSiblings={
        <>
          <ResendCode onClick={resendHandler} type="button" disabled={allowResendCounter > 0}>
            Resend Code
          </ResendCode>
          {allowResendCounter > 0 && (
            <ResendCodeTimer>
              {Math.floor(allowResendCounter / 1000 / 60)}:{String(Math.floor((allowResendCounter / 1000) % 60)).padStart(2, '0')}
            </ResendCodeTimer>
          )}
        </>
      }
    />
  );
};

export default FormAccountCode;
